import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { fmtMoney } from "../lib/api";
import Logo from "../components/Logo";
import { CheckCircle2, WifiOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUEUE_KEY = "core_oh_queue";

export default function PublicOpenHouse() {
  const { slug } = useParams();
  const [oh, setOh] = useState(null);
  const [sent, setSent] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [queueSize, setQueueSize] = useState(readQueue().length);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", role: "Buyer", permission: true });
  const flushing = useRef(false);

  useEffect(() => { axios.get(`${API}/public/open-house/${slug}`).then(r => setOh(r.data)).catch(() => setOh(false)); }, [slug]);

  useEffect(() => {
    const on = () => { setOnline(true); flushQueue(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    if (navigator.onLine) flushQueue();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    // eslint-disable-next-line
  }, []);

  const flushQueue = async () => {
    if (flushing.current) return;
    flushing.current = true;
    try {
      const q = readQueue();
      const remaining = [];
      for (const item of q) {
        try {
          await axios.post(`${API}/public/open-house/${item.slug}/signin`, item.body);
        } catch { remaining.push(item); }
      }
      writeQueue(remaining);
      setQueueSize(remaining.length);
      if (q.length > remaining.length) toast.success(`Synced ${q.length - remaining.length} offline sign-in(s)`);
    } finally { flushing.current = false; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!navigator.onLine) {
      const q = readQueue();
      q.push({ slug, body: form });
      writeQueue(q);
      setQueueSize(q.length);
      toast.success("Saved offline — will sync when back online");
      setSent(true);
      return;
    }
    try {
      await axios.post(`${API}/public/open-house/${slug}/signin`, form);
      setSent(true);
      toast.success("You're signed in — thanks!");
    } catch { toast.error("Sign-in failed"); }
  };

  if (oh === false) return <div className="min-h-screen flex items-center justify-center">Open house not found.</div>;
  if (!oh) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#ffffff]" data-testid="public-oh-page">
      <header className="bg-[#e1b7c1] px-5 py-6 text-center">
        <Logo variant="white" height={64} />
      </header>

      {!online && (
        <div className="bg-[#050303] text-white text-xs px-4 py-2 flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff size={13} /> Offline mode · sign-ins save on device and sync when connected {queueSize > 0 && `(${queueSize} pending)`}
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 py-6">
        {oh.image_url && <img src={oh.image_url} alt="" className="w-full h-52 object-cover rounded-lg" />}
        <div className="mt-4">
          <h1 className="font-heading text-3xl leading-tight">{oh.title}</h1>
          <div className="text-sm text-[#050303]/70 mt-1">{oh.address}</div>
          {oh.event_date && <div className="text-xs text-[#bf858e] font-semibold mt-1">{oh.event_date}</div>}
          {oh.price > 0 && <div className="font-heading text-2xl text-[#bf858e] mt-2">{fmtMoney(oh.price)}</div>}
          {oh.listing_note && <p className="text-sm text-[#050303]/85 mt-3 leading-relaxed">{oh.listing_note}</p>}
          <div className="mt-3 text-xs text-[#050303]/70 border-t border-[#d1cece] pt-3">
            {oh.hosting_agent_name && <>Hosted by <strong>{oh.hosting_agent_name}</strong><br /></>}
            {oh.listing_agent_name && <>Listed by <strong>{oh.listing_agent_name}</strong></>}
          </div>
        </div>

        <div className="mt-6 bg-white border border-[#d1cece] rounded-lg p-5" data-testid="signin-panel">
          {sent ? (
            <div className="text-center py-6" data-testid="signin-thanks">
              <CheckCircle2 size={40} className="text-[#bf858e] mx-auto mb-3" />
              <h3 className="font-heading text-2xl">Thank you!</h3>
              <p className="text-sm text-[#050303]/70 mt-2">Enjoy your visit. {form.permission ? "Check your email for details." : ""}</p>
              <button onClick={() => { setSent(false); setForm({ first_name: "", last_name: "", email: "", phone: "", role: "Buyer", permission: true }); }} className="mt-5 border border-[#050303] pill-btn text-sm" data-testid="signin-another">Sign in another guest</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="signin-form">
              <h3 className="font-heading text-xl">Sign in</h3>
              <div className="grid grid-cols-2 gap-2">
                <input required placeholder="First name" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="oh-first" autoComplete="given-name" />
                <input required placeholder="Last name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="oh-last" autoComplete="family-name" />
              </div>
              <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="oh-email" autoComplete="email" inputMode="email" />
              <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="oh-phone" inputMode="tel" autoComplete="tel" />
              <div>
                <div className="text-xs uppercase tracking-wider text-[#050303]/60 mb-2">I am a…</div>
                <div className="grid grid-cols-3 gap-2">
                  {["Buyer", "Seller", "Real Estate Agent"].map(r => (
                    <button type="button" key={r} onClick={() => setForm({ ...form, role: r })}
                      className={`py-2.5 rounded-full text-xs font-semibold border ${form.role === r ? "bg-[#050303] text-white border-[#050303]" : "bg-white border-[#d1cece] text-[#050303]/75"}`}
                      data-testid={`oh-role-${r.replace(/ /g, "-").toLowerCase()}`}>{r}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-[#050303]/80 pt-1" data-testid="oh-permission-label">
                <input type="checkbox" checked={form.permission} onChange={e => setForm({ ...form, permission: e.target.checked })} className="mt-1 accent-[#050303]" data-testid="oh-permission" />
                <span>Yes — send me updates about this listing and future opportunities from CORE Realty and Associates.</span>
              </label>
              <button type="submit" className="w-full bg-[#050303] text-white pill-btn text-base py-4" data-testid="oh-submit-signin">Sign in</button>
            </form>
          )}
        </div>

        <footer className="text-xs text-[#050303]/60 text-center mt-6 pb-8">
          <strong>CORE Realty and Associates LLC</strong><br />2440 Wisteria Drive, Suite 1564, Snellville, GA 30078<br />info@corerealtyandassociates.com · 404-317-8526
        </footer>
      </div>
    </div>
  );
}

function readQueue() { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; } }
function writeQueue(q) { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }
